import React, { type FC, useEffect, useState } from "react"
import { Box, HBox, VBox } from "@ui/box/Box"
import { Typo } from "@ui/text/Typo"
import { GD } from "src/GD"
import { S_UNIVERSAL_DIALOG } from "./universalDialog/signals"
import { FormParser } from "src/view/FormParser"
import { toast } from "react-toastify"
import { amountValidationError, parsePositiveAmountToMinorUnits } from "src/utils/amounts"
import { getFriendlyErrorMessage } from "src/utils/errors"
import { UserDetails } from "./UserDetails"
import { StatusBadge } from "./StatusBadge"
import { USER_ROLE } from "src/services/AuthService"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { DEFAULT_CURRENCY, MAX_USER_DETAILS_WIDTH } from "src/constants"
import { getTeamUserDisableSuccessMessage } from "src/utils/teamBalances"
import { buildBuyerCardFinancials, buildBuyerSpendMap } from "src/utils/buyerFinancials"
import { optionalRequest } from "src/utils/optionalRequest"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { useWindowSize } from "@ui/utils/useResize"
import { isMobileViewport } from "@ui/utils/mobileViewport"
import { ActionButton, Avatar, DS, EntityHeader, Section, Stage, StageButton, StageStat, FormIntro, Glyph } from "../ds/Kit"
import { TeamMemberCard } from "./TeamMemberCard"

interface TeamDetailsProps {
	team: ITeamSaved
	onClose: () => void
	onUpdate: () => void
}

const MAX_TEAM_MODAL_WIDTH = 900
const TEAM_DETAILS_DIALOG_SX = {
	padding: "0px",
	width: `min(${MAX_TEAM_MODAL_WIDTH}px, calc(100vw - 32px))`,
	maxWidth: `${MAX_TEAM_MODAL_WIDTH}px`
}

const BUYER_STATS_LIMIT = 1000

const MembersEmptyState: FC<{ onAdd: () => void }> = ({ onAdd }) => (
	<VBox gap='12px' sx={{ alignItems: "center", textAlign: "center", padding: "36px 16px 32px" }}>
		<Box
			aria-hidden
			sx={{
				width: "52px",
				height: "52px",
				borderRadius: "16px",
				background: DS.accentSoft,
				color: DS.accent,
				display: "flex",
				alignItems: "center",
				justifyContent: "center"
			}}
		>
			<svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
				<path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
				<circle cx='9' cy='7' r='4' />
				<path d='M23 21v-2a4 4 0 0 0-3-3.87' />
				<path d='M16 3.13a4 4 0 0 1 0 7.75' />
			</svg>
		</Box>
		<VBox gap='4px'>
			<Typo sx={{ fontSize: "15px", fontWeight: 650, color: DS.ink }}>No members yet</Typo>
			<Typo sx={{ fontSize: "13px", color: DS.muted }}>Add a user to give them access to this team.</Typo>
		</VBox>
		<ActionButton tone='primary' onClick={onAdd}>Add first member</ActionButton>
	</VBox>
)

export const TeamDetails: React.FC<TeamDetailsProps> = ({
	team: initialTeam,
	onClose,
	onUpdate
}) => {
	const [profile] = GD.REQ_PROFILE.useRequest()
	const issuerContext = useOptionalIssuer()
	const selectedIssuerId = issuerContext?.selectedIssuerId ?? null
	const issuerCurrency = issuerContext?.selectedIssuer?.currency || DEFAULT_CURRENCY
	const { width: viewportWidth, height: viewportHeight } = useWindowSize()
	const isMobile = isMobileViewport(viewportWidth, viewportHeight)
	const [team, setTeam] = useState(initialTeam)
	const [members, setMembers] = useState<ITeamUser[]>([])
	const [membersState, setMembersState] = useState<"loading" | "ready" | "error">("loading")
	const [buyerCards, setBuyerCards] = useState<Map<string, { cardsCount: number; cardsBalance: number }>>(new Map())
	const [buyerSpend, setBuyerSpend] = useState<Map<string, number>>(new Map())
	const [buyerCardsLoaded, setBuyerCardsLoaded] = useState(false)
	const [buyerSpendLoaded, setBuyerSpendLoaded] = useState(false)
	const [isEditing, setIsEditing] = useState(false)

	useEffect(() => {
		loadMembers()
	}, [team.uid])

	const loadMembers = async () => {
		setBuyerCardsLoaded(false)
		setBuyerSpendLoaded(false)
		const [membersResponse, cardsResponse, spendResponse] = await Promise.all([
			GD.REQ_TEAM_USERS.request({ uid: team.uid }),
			optionalRequest(() => GD.REQ_CARDS.request()),
			optionalRequest(() => GD.REQ_TEAM_STATS_TOP_SPENDERS.request({
				uid: team.uid,
				limit: BUYER_STATS_LIMIT,
				currency: issuerCurrency,
				issuer: selectedIssuerId ?? undefined
			}))
		])
		if (membersResponse?.data) {
			setMembers(membersResponse.data)
			setMembersState("ready")
		} else {
			setMembersState("error")
		}
		if (cardsResponse?.data) {
			setBuyerCards(buildBuyerCardFinancials(cardsResponse.data))
			setBuyerCardsLoaded(true)
		}
		if (spendResponse?.data) {
			setBuyerSpend(buildBuyerSpendMap(spendResponse.data.buyers))
			setBuyerSpendLoaded(true)
		}
	}

	const handleAddMember = async () => {
		const usersRes = await GD.REQ_USERS_LIST_TEAM.request({ team_uid: team.uid })
		if (!usersRes.data) {
			toast.error("Could not load users list")
			return
		}

		const existingIds = new Set(members.map((m) => m.uid))
		const availableUsers = usersRes.data
			.filter((u) => !existingIds.has(u.uid))
			.map((u) => ({ name: `${u.name} (${u.email}) - ${u.company_role}`, value: u.uid }))

		S_UNIVERSAL_DIALOG.invoke({
			label: "Add Member to Team",
			contentPadding: isMobile ? "16px 16px 0" : "24px",
			content: (
				<>
					<FormIntro icon={Glyph.invite} title="Add a team member" description="Pick a user to join this team. They get access right away." />
					<FormParser
						onSubmit={async (data) => {
							const res = await GD.REQ_TEAM_USER_ADD.request({
								uid: team.uid,
								user_uid: data.user_uid,
								team_uid: team.uid
							})

							if (res.error) return res

							toast.success("User added successfully")

							S_UNIVERSAL_DIALOG.invoke(null)
							setTimeout(() => {
								S_UNIVERSAL_DIALOG.invoke({
									sx: TEAM_DETAILS_DIALOG_SX,
									content: (
										<TeamDetails team={team} onClose={onClose} onUpdate={onUpdate} />
									),
									closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
								})
							}, 100)

							return {}
						}}
						onRequest={async () => {
							return {
								user_uid: "",
								$__user_uid_type: "select",
								$__user_uid_values: availableUsers,
								$__decorator: { title: "Select User" }
							}
						}}
						primaryButton={{ title: "Add" }}
						secondaryButton={{
							title: "Cancel",
							onClick: () => {
								S_UNIVERSAL_DIALOG.invoke({
									sx: TEAM_DETAILS_DIALOG_SX,
									content: (
										<TeamDetails team={team} onClose={onClose} onUpdate={onUpdate} />
									),
									closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
								})
							}
						}}
					/>
				</>
			),
			closeDialog: () => {
				S_UNIVERSAL_DIALOG.invoke(null)
			}
		})
	}

	const handleDisableUser = async (user_uid: string) => {
		if (!confirm("Are you sure you want to disable this user in the team?")) return

		const response = await GD.REQ_TEAM_USER_DISABLE.request({ uid: team.uid, user_uid })
		if (response.error) {
			toast.error(getFriendlyErrorMessage(response.error, "Failed to update user status"))
			return
		}
		toast.success(getTeamUserDisableSuccessMessage(response.data.returned))
		const [teamResponse] = await Promise.all([
			GD.REQ_TEAM_GET.request(team.uid),
			loadMembers()
		])
		if (teamResponse.data) setTeam(teamResponse.data)
		onUpdate()
	}

	const handleUserClick = async (user_uid: string) => {
		S_UNIVERSAL_DIALOG.invoke({
			sx: { width: `min(${MAX_USER_DETAILS_WIDTH}px, calc(100vw - 32px))`, maxWidth: `${MAX_USER_DETAILS_WIDTH}px` },
			contentPadding: "0px",
			content: (
				<UserDetails
					userUid={user_uid}
					onClose={() => {
						S_UNIVERSAL_DIALOG.invoke({
							sx: TEAM_DETAILS_DIALOG_SX,
							content: <TeamDetails team={team} onClose={onClose} onUpdate={onUpdate} />,
							closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
						})
					}}
					onUpdate={() => {
						loadMembers()
					}}
				/>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}

	const handleTopUp = (user: ITeamUser) => {
		S_UNIVERSAL_DIALOG.invoke({
			label: `Top Up ${user.name}`,
			content: (
				<FormParser
					key='teamUserTopUp'
					onSubmit={async (data) => {
						const parsedAmount = parsePositiveAmountToMinorUnits(data.amount)
						if (typeof parsedAmount.amount !== "number")
							return amountValidationError(parsedAmount.error || "Enter a valid amount.")
						const res = await GD.REQ_TEAM_USER_TOPUP.request({
							uid: team.uid,
							user_uid: user.uid,
							amount: parsedAmount.amount,
							currency: issuerCurrency,
							issuer: selectedIssuerId ?? undefined
						})

						if (res.error) return res

						toast.success(`Top up for ${user.name} successful`)
						const refreshedTeam = await GD.REQ_TEAM_GET.request(team.uid)
						const nextTeam = refreshedTeam.data || team
						if (refreshedTeam.data) setTeam(refreshedTeam.data)
						await loadMembers()
						onUpdate()
						S_UNIVERSAL_DIALOG.invoke(null)
						setTimeout(() => {
							S_UNIVERSAL_DIALOG.invoke({
								sx: TEAM_DETAILS_DIALOG_SX,
								content: (
									<TeamDetails team={nextTeam} onClose={onClose} onUpdate={onUpdate} />
								),
								closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
							})
						}, 100)

						return {}
					}}
					onRequest={async () => {
						const template = await GD.REQ_USER_TOPUP_TEMPLATE.request()
						return { ...template.data!, $__issuer_hidden: true, $__issuer_label_hidden: true }
					}}
					primaryButton={{ title: "Top Up" }}
					secondaryButton={{
						title: "Cancel",
						onClick: () => {
							S_UNIVERSAL_DIALOG.invoke({
								sx: TEAM_DETAILS_DIALOG_SX,
								content: (
									<TeamDetails team={team} onClose={onClose} onUpdate={onUpdate} />
								),
								closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
							})
						}
					}}
				/>
			),
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
		})
	}


	const closeButton = (
		<Box
			element='button'
			type='button'
			aria-label='Close team details'
			onClick={onClose}
			sx={{
				position: "absolute",
				top: "18px",
				right: "24px",
				width: "34px",
				height: "34px",
				padding: 0,
				borderRadius: "10px",
				cursor: "pointer",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: DS.soft,
				border: "1px solid transparent",
				color: DS.muted,
				transition: "all 0.15s",
				zIndex: 1,
				[DS.mobile]: { top: "14px", right: "14px" },
				"&:hover": { backgroundColor: DS.accentSoft, color: DS.accentInk }
			}}
		>
			<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
				<line x1='18' y1='6' x2='6' y2='18' />
				<line x1='6' y1='6' x2='18' y2='18' />
			</svg>
		</Box>
	)

	const headerPadding = isMobile ? "16px 16px 14px" : "22px 24px 18px"
	const bodyPadding = isMobile ? "14px 16px 20px" : "20px 24px 24px"

	if (isEditing) {
		return (
			<VBox
				gap='0'
				sx={{ width: "100%", maxWidth: `${MAX_TEAM_MODAL_WIDTH}px`, minWidth: 0, backgroundColor: DS.surface }}
			>
				<Box sx={{ position: "relative", padding: headerPadding, borderBottom: `1px solid ${DS.line}` }}>
					<EntityHeader
						leading={<Avatar name={team.name} size={44} />}
						title='Edit team'
						meta={team.name}
					/>
				</Box>
				<Box sx={{ padding: bodyPadding }}>
					<FormParser
						onSubmit={async (data) => {
							const res = await GD.REQ_TEAM_UPDATE.request({ ...team, name: data.name })
							if (res.error) return res

							const newTeam = { ...team, name: data.name }
							setTeam(newTeam)
							onUpdate()
							setIsEditing(false)
							toast.success("Team updated")
							return {}
						}}
						onRequest={async () => ({
							name: team.name,
							$__decorator: { title: "Edit Team Details" }
						})}
						primaryButton={{ title: "Save Changes" }}
						secondaryButton={{ title: "Cancel", onClick: () => setIsEditing(false) }}
					/>
				</Box>
			</VBox>
		)
	}

	// ─── Derived values ───────────────────────────────────────────────────────
	const activeMembers = members.filter((m) => m.team_status === "active").length
	const totalBalance = team.balance ?? 0
	const createdDate = team.created_at
		? new Date(team.created_at).toLocaleDateString("en-GB", {
				year: "numeric",
				month: "short",
				day: "2-digit"
			})
		: null
	const totalCards = buyerCardsLoaded
		? members.reduce((sum, m) => sum + (buyerCards.get(m.uid)?.cardsCount ?? 0), 0)
		: undefined
	const totalOnCards = buyerCardsLoaded
		? members.reduce((sum, m) => sum + (buyerCards.get(m.uid)?.cardsBalance ?? 0), 0)
		: undefined
	const totalSpent = buyerSpendLoaded
		? members.reduce((sum, m) => sum + (buyerSpend.get(m.uid) ?? 0), 0)
		: undefined
	const formatMoney = (amount: number | undefined) =>
		amount === undefined ? "—" : formatCurrencyWithNumberToSymbolWithNumber(amount, issuerCurrency)

	const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const

	return (
		<VBox gap='0' sx={{ width: "100%", maxWidth: `${MAX_TEAM_MODAL_WIDTH}px`, minWidth: 0, overflowX: "hidden", backgroundColor: DS.soft }}>
			<Box
				sx={{
					position: "relative",
					backgroundColor: DS.surface,
					borderBottom: `1px solid ${DS.line}`,
					padding: headerPadding,
					flexShrink: 0
				}}
			>
				{closeButton}
				<EntityHeader
					leading={<Avatar name={team.name} size={52} />}
					title={team.name}
					badge={<StatusBadge status={team.status || "unknown"} />}
					meta={createdDate ? `Team · Created ${createdDate}` : "Team"}
				/>
			</Box>

			<VBox gap='14px' sx={{ padding: bodyPadding, minWidth: 0 }}>
				<Stage>
					<VBox gap='18px' sx={{ minWidth: 0, padding: "4px 4px 4px 8px", [DS.mobile]: { padding: 0 } }}>
						<VBox gap='6px'>
							<Typo sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.muted }}>Team balance</Typo>
							<Typo sx={{ fontSize: "44px", lineHeight: 1.05, fontWeight: 700, letterSpacing: "-0.035em", color: DS.ink, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere", [DS.mobile]: { fontSize: "36px" } }}>
								{formatCurrencyWithNumberToSymbolWithNumber(totalBalance, issuerCurrency)}
							</Typo>
							<Typo sx={{ fontSize: "13px", color: DS.muted }}>Total funds held by this team</Typo>
						</VBox>
						<HBox gap='10px' sx={{ flexWrap: "wrap" }}>
							<StageButton onClick={handleAddMember}>
								<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' {...stroke}><path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' /><circle cx='9' cy='7' r='4' /><path d='M19 8v6M22 11h-6' /></svg>
								Add Member
							</StageButton>
							<StageButton ghost onClick={() => setIsEditing(true)}>
								<svg aria-hidden width='16' height='16' viewBox='0 0 24 24' {...stroke}><path d='M12 20h9' /><path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' /></svg>
								Edit Team
							</StageButton>
						</HBox>
					</VBox>
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
						<StageStat label='Members'>
							{members.length}
							<StageHint>{`${activeMembers} active`}</StageHint>
						</StageStat>
						<StageStat label='Buyers'>{team.buyers_count ?? 0}</StageStat>
						<StageStat label='Cards'>
							{totalCards ?? "—"}
							{totalOnCards !== undefined && <StageHint>{`${formatMoney(totalOnCards)} on cards`}</StageHint>}
						</StageStat>
						<StageStat label='Spent'>{formatMoney(totalSpent)}</StageStat>
					</Box>
				</Stage>

				<Section
					title='Members'
					action={
						<Typo sx={{ fontSize: "12px", fontWeight: 600, color: DS.accentInk, background: DS.accentSoft, borderRadius: "999px", padding: "2px 10px" }}>
							{members.length}
						</Typo>
					}
				>
					{membersState !== "ready" ? (
						<Typo sx={{ padding: "28px 0", textAlign: "center", color: membersState === "error" ? "#B91C1C" : DS.muted, fontSize: "13px" }}>
							{membersState === "error" ? "Could not load team members. Close and reopen to try again." : "Loading members…"}
						</Typo>
					) : members.length === 0 ? (
						<MembersEmptyState onAdd={handleAddMember} />
					) : (
						<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px", padding: "4px 0 16px" }}>
							{members.map((row) => {
								const cardFinancials = buyerCards.get(row.uid)
								const isBuyer = (row.company_role || "").toLowerCase().includes(USER_ROLE.BUYER)
								const canManageBuyer = isBuyer && profile?.company_role !== USER_ROLE.BUYER
								const spent = buyerSpendLoaded ? buyerSpend.get(row.uid) : undefined
								const cardsCount = cardFinancials?.cardsCount ?? (buyerCardsLoaded ? 0 : undefined)
								const cardsBalance = cardFinancials?.cardsBalance ?? (buyerCardsLoaded ? 0 : undefined)
								const active = row.team_status === "active"
								return (
									<TeamMemberCard
										key={row.uid}
										name={row.name}
										email={row.email}
										role={row.company_role}
										status={row.team_status}
										joined={row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" }) : null}
										stats={isBuyer ? [
											{ label: "Available", value: formatMoney(row.team_balance) },
											{ label: "Spent", value: formatMoney(spent) },
											{ label: "Cards", value: cardsCount === undefined ? "—" : String(cardsCount) },
											{ label: "On cards", value: formatMoney(cardsBalance) }
										] : undefined}
										onOpen={() => handleUserClick(row.uid)}
										onTopUp={active && canManageBuyer ? () => handleTopUp(row) : undefined}
										removeLabel={row.status === "active" ? "Remove" : "Enable"}
										onRemove={active ? () => handleDisableUser(row.uid) : undefined}
									/>
								)
							})}
						</Box>
					)}
				</Section>
			</VBox>
		</VBox>
	)
}

const StageHint = ({ children }: { children: string }) => (
	<Box element='span' sx={{ display: "block", marginTop: "2px", fontSize: "11px", fontWeight: 500, color: DS.muted, whiteSpace: "normal" }}>{children}</Box>
)
